import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';
import { Button, Modal, ModalHeader, PriceInput } from '@spotahome/ui-library';

import Success from '@spotahome/marketplace-common/src/components/icons/Success';

import SuggestPriceModalIcon from './SuggestPriceModalIcon';

import './SuggestPriceModal.scss';

const SUCCESS_KEYS = {
  LOWER: {
    title: 'listing.pricing.suggest.success.title.lower',
    description: 'listing.pricing.suggest.success.description.lower',
    ctaText: 'listing.pricing.suggest.success.cta.lower'
  },
  HIGHER: {
    title: 'listing.pricing.suggest.success.title.higherorequal',
    description: 'listing.pricing.suggest.success.description.higherorequal',
    ctaText: 'listing.pricing.suggest.success.cta.higherorequal'
  }
};

const renderSuggestionHeader = (title, description, icon) => (
  <Fragment>
    <h3 className="suggest-price-modal__title">{title}</h3>
    <div className="suggest-price-modal__icon">{icon}</div>
    {description ? (
      <p className="suggest-price-modal__description">{description}</p>
    ) : null}
  </Fragment>
);

const renderSuccessContent = (keys, onClose) => (
  <div>
    {renderSuggestionHeader(
      trans(keys.title),
      trans(keys.description),
      <Success />
    )}
    <Button
      dataTest="success-button"
      className="suggest-price-modal__cta"
      onClick={onClose}
    >
      {trans(keys.ctaText)}
    </Button>
  </div>
);

const SuggestPriceModal = ({
  isOpen,
  handleOfferIsMade,
  onModalClose,
  currencyIsoCode,
  price,
  handleEqualOrHigherPriceSuggestion
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [offer, setOffer] = useState(null);

  const handleOnChange = ({ value }) => {
    const sanitizedValue = value.replace(/[.,]/g, '');
    if (isNaN(Number(sanitizedValue))) {
      return;
    }
    setOffer(parseInt(sanitizedValue, 10));
  };

  const handleModalCloseAfterSuggestion = () => {
    onModalClose();
    if (offer >= price) {
      handleEqualOrHigherPriceSuggestion();
    }
  };

  const handleMakeOffer = async () => {
    if (!offer) {
      return;
    }
    try {
      await handleOfferIsMade(offer);
      setShowSuccess(true);
    } catch (error) {
      // TO-DO: Handle error and give feedback to user
      console.log(error);
    }
  };

  const renderSuggestion = () => (
    <div className="suggest-price-modal--variant">
      {renderSuggestionHeader(
        trans('listing.pricing.suggest.suggestion.title'),
        '',
        <SuggestPriceModalIcon currencyIsoCode={currencyIsoCode} />
      )}
      <PriceInput
        title={trans('listing.pricing.suggest.input-label')}
        name="offer"
        id="offer"
        handleOnChange={handleOnChange}
        currencyIsoCode={currencyIsoCode}
        value={offer}
        placeholder="0"
      />

      <Button
        className="suggest-price-modal__cta"
        disabled={Number(offer) === 0 || !offer}
        onClick={handleMakeOffer}
        type="button"
        dataTest="suggest-button"
      >
        {trans('listing.pricing.suggest.button1')}
      </Button>
    </div>
  );

  const renderSuccess = () => {
    const keys = offer < price ? SUCCESS_KEYS.LOWER : SUCCESS_KEYS.HIGHER;

    return renderSuccessContent(keys, handleModalCloseAfterSuggestion);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onModalClose}
      ariaHideApp={false}
      className="suggest-price-modal"
    >
      <ModalHeader
        onRequestClose={onModalClose}
        showButton
        dataTest="suggest-price-modal"
        className="suggest-price-modal__header"
      />
      {showSuccess ? renderSuccess() : renderSuggestion()}
    </Modal>
  );
};

SuggestPriceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleOfferIsMade: PropTypes.func.isRequired,
  onModalClose: PropTypes.func.isRequired,
  currencyIsoCode: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  handleEqualOrHigherPriceSuggestion: PropTypes.func.isRequired
};

export default SuggestPriceModal;
